import { HttpClient, HttpErrorResponse, HttpHeaders, HttpResponse } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { Observable, Subject, throwError } from "rxjs";
import { catchError, map, switchMap } from "rxjs/operators";
import { AutomapperService } from "../../../../../core/automapper/automapper.service";
import { BASE_API_URL } from "../../../../../core/environment.tokens";
import { CommentItem } from "../../../../../shared/comments/comment-item/comment-item";
import { ListItem } from "../../../../../shared/list/list-item";
import { EditPendService } from "../../../../../shared/pend/edit-pend.service";
import { IChaseTimelineResponseV1 } from "../../../../../shared/timeline/chase-timeline-item-v1.model";
import { toCommentItems, toTimelineItems } from "../../../../../shared/timeline/chase-timeline-v1.mapper";
import { TimelineItem } from "../../../../../shared/timeline/timeline-item.model";
import { PendListItem } from "../../internal-pends-listing/pend-list-item.model";
import { createPendAnnotationRequest } from "../create-pend-annotation-request";
import { ProviderPacketItem } from "./provider-packet-item.model";
import { ProviderPacketResponseItem } from "./provider-packet-response-packet-item.model";

@Injectable()
export class InternalPendsDetailInfoService {
  reset = new Subject<any>();
  refresh = new Subject<any>();
  constructor(
    @Inject(BASE_API_URL) private readonly baseApiUrl: string,
    private http: HttpClient,
    private automapper: AutomapperService,
    private route: ActivatedRoute,
    private editPendService: EditPendService
  ) { }

  getPendGdFromPath(): number {
    return this.route.snapshot.params.pendGd;
  }

  resetFunction(pendId: number) {

    this.reset.next(pendId);
  }

  refreshFunction() {

    this.refresh.next();
  }

  getSummaryItems(pendId): Observable<ListItem[]> {
    const url = `${this.baseApiUrl}Pend?pendId=${pendId}`;

    return this.http.get(url).pipe(
      map(this.automapper.curryMany("default", "ListItem"))
    );
  }

  // Reads now go through the V1 chase timeline endpoint so DynamoDB-stored comments show up
  // alongside SQL Server comments.
  getComments(pendId: number): Observable<CommentItem[]> {
    return this.editPendService.getChasePendData(+pendId).pipe(
      switchMap(chasePend => {
        // "LATEST COMMENT" widget - not every recent pend event is a comment (e.g. status changes) and filtering on event type lives on mapper.
        const url = `${this.baseApiUrl}v1chases/${chasePend.chaseId}/timeline?resources=pend&resourceIds=${pendId}&pageSize=10`;
        return this.http.get<IChaseTimelineResponseV1>(url);
      }),
      map(response => toCommentItems(response && response.items).slice(0, 1))
    );
  }

  getTimelineItems(pendId: number): Observable<TimelineItem[]> {
    return this.editPendService.getChasePendData(+pendId).pipe(
      switchMap(chasePend => {
        // Matches the legacy Timeline/GetPendTimeline endpoint's maxRecords=5.
        const url = `${this.baseApiUrl}v1chases/${chasePend.chaseId}/timeline?resources=pend&resourceIds=${pendId}&pageSize=5`;
        return this.http.get<IChaseTimelineResponseV1>(url);
      }),
      map(response => toTimelineItems(response && response.items))
    );
  }

  saveComment(pendId: number, chaseId: number, commentText: string) {
    const url = `${this.baseApiUrl}v1chases/${chaseId}/annotations`;
    const request = createPendAnnotationRequest(pendId, commentText);

    return this.http.post(url, request)
      .pipe(
        catchError(this.handleError) // then handle the error
      );
  }

  getPendId(pendListItem: PendListItem, isNext: boolean): Observable<number> {
    const url = `${this.baseApiUrl}Pend/GetPreviousNextPend?isNext=${isNext}`;


    return this.http.post(url, pendListItem).pipe(map((response: any) => response as number));
  }

  handleError(error: HttpErrorResponse) {
    if (error.error instanceof ErrorEvent) {
      // A client-side or network error occurred. Handle it accordingly.
      console.error("An error occurred:", error.error.message);
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong,
      console.error(
        `Backend returned code ${error.status}, ` +
        `body was: ${error.error}`);
    }

    // return an ErrorObservable with a user-facing error message
    return throwError(
      "Something bad happened; please try again later.");
  }

  printProviderPacket(providerPacketItem: ProviderPacketItem): Observable<string> {
    const url = `${this.baseApiUrl}pend/providerpacket`;

    const headers = new HttpHeaders().set("content-type", "application/json");

    return this.http.post(url, providerPacketItem, { headers }).pipe(
      map((response: any) => response as string)
    );
  }

}
